import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { ordersApi } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import { BenchmarkPerformanceDataMap } from '@/types/client';
import PerformanceCell from '@/components/utils/PerformanceCell';
import SchemePerfBadges from '@/components/utils/SchemePerfBadges';
import { benchmarkOrder } from '@/components/utils/benchmarkConstants';
import { Surface, Button, Pill, Note, Placeholder } from '@/components/ui';
import styles from './PortfolioPerformance.module.css';
import shared from '@/components/utils/shared.module.css';

interface MergedScheme {
    schemeId: string;
    scheme_code: string;
    display_name?: string;
    full_name?: string;
    has_delayed_nav?: boolean;
    xirr_percent: number | null;
    xirr_status: string;
    xirr_msg: string;
    twrr_percent: number | null;
    twrr_status: string;
    twrr_msg: string;
}

const getTodayString = () => new Date().toISOString().split('T')[0];

const CustomRangePerformance: React.FC = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [schemes, setSchemes] = useState<MergedScheme[]>([]);
    const [benchmarks, setBenchmarks] = useState<BenchmarkPerformanceDataMap | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The range the currently-shown data was fetched for (drives the range chip).
    const [fetchedRange, setFetchedRange] = useState<{ start: string; end: string } | null>(null);

    const handleFetch = async () => {
        const today = getTodayString();
        if (!startDate || !endDate) {
            setError('Please select both start and end dates.');
            return;
        }
        if (startDate >= endDate) {
            setError('Start date must be before end date.');
            return;
        }
        if (endDate > today) {
            setError('End date cannot be in the future.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const params = { keys: 'custom', start_date: startDate, end_date: endDate };
            const [xirrResponse, twrrResponse, benchmarkResponse] = await Promise.all([
                ordersApi.getPortfolioPerformanceXIRR(params),
                ordersApi.getPortfolioPerformanceTWRR(params),
                ordersApi.getBenchmarkPerformance(params),
            ]);

            const xirrData = xirrResponse.custom ?? {};
            const twrrData = twrrResponse.custom ?? {};

            const allIds = new Set([...Object.keys(xirrData), ...Object.keys(twrrData)]);
            const merged: MergedScheme[] = Array.from(allIds).map((id): MergedScheme | null => {
                const xirr = xirrData[id];
                const twrr = twrrData[id];
                const source = xirr ?? twrr;
                if (!source) return null;
                const xirrPeriod = xirr?.custom_range;
                const twrrPeriod = twrr?.custom_range;
                return {
                    schemeId: id,
                    scheme_code: source.scheme_code,
                    display_name: source.display_name,
                    full_name: source.full_name,
                    has_delayed_nav: source.has_delayed_nav,
                    xirr_percent: xirrPeriod?.xirr_percent ?? null,
                    xirr_status: xirrPeriod?.status ?? 'failure',
                    xirr_msg: xirrPeriod?.msg ?? '',
                    twrr_percent: twrrPeriod?.twrr_percent ?? null,
                    twrr_status: twrrPeriod?.status ?? 'failure',
                    twrr_msg: twrrPeriod?.msg ?? '',
                };
            }).filter((s): s is MergedScheme => s !== null);

            setSchemes(merged);
            setBenchmarks(benchmarkResponse.custom ?? null);
            setFetchedRange({ start: startDate, end: endDate });
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load custom range performance data');
        } finally {
            setLoading(false);
        }
    };

    const sortedBenchmarks = benchmarks
        ? Object.entries(benchmarks).sort(([, a], [, b]) => {
              const idxA = benchmarkOrder.indexOf(a.symbol);
              const idxB = benchmarkOrder.indexOf(b.symbol);
              return idxA - idxB;
          })
        : [];

    const rangeChip = fetchedRange && (
        <Pill tone="neutral" solid>
            {formatDate(fetchedRange.start)} → {formatDate(fetchedRange.end)}
        </Pill>
    );

    const hasData = !loading && schemes.length > 0;

    return (
        <>
            {/* Toolbar mirrors the region toolbars: inline dates + action, right-aligned */}
            <Surface padded>
            <div className={styles['region-toolbar']}>
                <div className={shared['filter-group']}>
                    <label htmlFor="cr-start-date">From</label>
                    <input
                        id="cr-start-date"
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        max={endDate || getTodayString()}
                        className={shared.input}
                    />
                </div>
                <div className={shared['filter-group']}>
                    <label htmlFor="cr-end-date">To</label>
                    <input
                        id="cr-end-date"
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        min={startDate || undefined}
                        max={getTodayString()}
                        className={shared.input}
                    />
                </div>
                <Button variant="primary" onClick={handleFetch} disabled={loading}>
                    {loading ? 'Loading…' : 'Fetch'}
                </Button>
            </div>
            </Surface>

            <Note tone="info">
                For <strong>SIFEF</strong>, use clean period boundaries to get accurate data — e.g.{' '}
                <strong>1st Jan 2026 → 31st Jan 2026</strong>.
            </Note>

            {error && <Note tone="error">{error}</Note>}

            {loading && (
                <Surface>
                    <Placeholder kind="loading" message="Loading custom range data…" />
                </Surface>
            )}

            {!loading && !error && schemes.length === 0 && (
                <Surface>
                    <Placeholder message="No custom-range performance yet." hint="Pick a date range above and click Fetch." />
                </Surface>
            )}

            {hasData && (
                <div className={styles['grid-container']}>
                    <Surface title="Schemes" meta={rangeChip}>
                            <div className={styles['table-scroll']}>
                                <table className={`${shared.table} ${styles['portfolio-performance-table']}`}>
                                    <thead>
                                        <tr>
                                            <th className={styles['scheme-code']}>Scheme</th>
                                            <th className={styles['period-column']}>TWRR (%)</th>
                                            <th className={styles['period-column']}>XIRR (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schemes.map((scheme, index) => (
                                            <tr key={scheme.schemeId} className={index % 2 === 0 ? styles['row-even'] : styles['row-odd']}>
                                                <td className={styles['scheme-code']}>
                                                    <span className={styles['scheme-cell-inner']}>
                                                        <Tooltip
                                                            title={scheme.full_name || ''}
                                                            placement="top"
                                                            arrow
                                                            disableHoverListener={!scheme.full_name}
                                                            disableTouchListener={!scheme.full_name}
                                                            disableFocusListener={!scheme.full_name}
                                                        >
                                                            <span
                                                                className={styles['scheme-cell-text']}
                                                                style={{ cursor: scheme.full_name ? 'help' : 'default' }}
                                                            >
                                                                {scheme.scheme_code}
                                                                {scheme.display_name && ` (${scheme.display_name})`}
                                                            </span>
                                                        </Tooltip>

                                                        <SchemePerfBadges scheme={scheme} />
                                                    </span>
                                                </td>
                                                <PerformanceCell
                                                    value={scheme.twrr_percent}
                                                    status={scheme.twrr_status}
                                                    msg={scheme.twrr_msg}
                                                />
                                                <PerformanceCell
                                                    value={scheme.xirr_percent}
                                                    status={scheme.xirr_status}
                                                    msg={scheme.xirr_msg}
                                                />
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                    </Surface>

                    {sortedBenchmarks.length > 0 && (
                        <Surface title="Benchmarks" meta={rangeChip}>
                                <div className={styles['table-scroll']}>
                                    <table className={`${shared.table} ${styles['portfolio-performance-table']}`}>
                                        <thead>
                                            <tr>
                                                <th className={styles['scheme-column']}>Name</th>
                                                <th className={styles['period-column']}>Change (%)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedBenchmarks.map(([benchmarkId, info], index) => {
                                                const period = info.custom_range;
                                                return (
                                                    <tr key={benchmarkId} className={index % 2 === 0 ? styles['row-even'] : styles['row-odd']}>
                                                        <td className={styles['scheme-code']}>{info.name}</td>
                                                        <PerformanceCell
                                                            value={period?.change_percent ?? null}
                                                            status={period?.status ?? 'failure'}
                                                            msg={period?.msg ?? ''}
                                                        />
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                        </Surface>
                    )}
                </div>
            )}
        </>
    );
};

export default CustomRangePerformance;